import React from 'react';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';

export default function PrivacyPolicy() {
  return (
    <div className="relative min-h-screen bg-rax-bg overflow-x-hidden">
      <Navbar />
      <main className="relative z-10 mx-auto max-w-4xl px-6 pb-24 pt-32">
        <h1 className="mb-2 font-heading text-3xl font-bold text-rax-heading">Privacy Policy</h1>
        <p className="mb-10 text-sm text-rax-body/60">Last updated: March 22, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed text-rax-body">

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">1. Introduction</h2>
            <p>
              RAX Protocol Foundation ("Company," "we," "us," or "our") operates the RAX Protocol platform at
              raxprotocol.xyz. This Privacy Policy explains how we collect, use, disclose, and protect
              information when you access or use our website and services (collectively, the "Service").
            </p>
            <p className="mt-3">
              By using the Service, you agree to the collection and use of information in accordance with
              this Privacy Policy. If you do not agree, please discontinue use of the Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">2. Information We Collect</h2>

            <h3 className="mb-2 mt-4 font-heading text-base font-semibold text-rax-heading">2.1 Information You Provide</h3>
            <p>We may collect information you voluntarily provide, including:</p>
            <ul className="mt-2 list-disc space-y-1.5 pl-5">
              <li>Email address (if you subscribe to updates or contact us);</li>
              <li>Wallet addresses (public blockchain addresses you connect to the Service);</li>
              <li>Communications you send us via email or support channels.</li>
            </ul>

            <h3 className="mb-2 mt-4 font-heading text-base font-semibold text-rax-heading">2.2 Automatically Collected Information</h3>
            <p>When you visit raxprotocol.xyz, we may automatically collect:</p>
            <ul className="mt-2 list-disc space-y-1.5 pl-5">
              <li>IP address and approximate geographic location;</li>
              <li>Browser type, version, and operating system;</li>
              <li>Pages visited, time spent, and referring URLs;</li>
              <li>Device identifiers and session data;</li>
              <li>Log files and usage analytics.</li>
            </ul>

            <h3 className="mb-2 mt-4 font-heading text-base font-semibold text-rax-heading">2.3 Blockchain Data</h3>
            <p>
              Transactions on public blockchains are publicly visible by nature. When you interact with
              RAX Protocol smart contracts, your wallet address and transaction data are recorded permanently
              on-chain. This information is publicly accessible and outside our control.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li>Provide, maintain, and improve the Service;</li>
              <li>Respond to your inquiries and provide support;</li>
              <li>Send service-related communications (with your consent where required);</li>
              <li>Monitor usage patterns and optimize user experience;</li>
              <li>Detect, prevent, and address fraud, security issues, or prohibited activity;</li>
              <li>Comply with applicable laws, regulations, and legal obligations;</li>
              <li>Enforce our Terms of Service.</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">4. Cookies and Tracking Technologies</h2>
            <p>
              We may use cookies, web beacons, and similar tracking technologies to enhance your experience
              on raxprotocol.xyz. These may include:
            </p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li><strong className="text-rax-heading">Essential cookies:</strong> Necessary for basic site functionality;</li>
              <li><strong className="text-rax-heading">Analytics cookies:</strong> Help us understand how visitors interact with the site (e.g., Google Analytics or similar);</li>
              <li><strong className="text-rax-heading">Preference cookies:</strong> Remember your settings and preferences.</li>
            </ul>
            <p className="mt-3">
              You can control cookies through your browser settings. Disabling certain cookies may affect
              site functionality.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">5. Sharing of Information</h2>
            <p>We do not sell your personal information. We may share information with:</p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li>
                <strong className="text-rax-heading">Service Providers:</strong> Third-party vendors who assist in operating the Service
                (e.g., hosting, analytics, email delivery), bound by confidentiality obligations;
              </li>
              <li>
                <strong className="text-rax-heading">Legal Compliance:</strong> Government authorities or law enforcement when required
                by law, court order, or to protect legal rights;
              </li>
              <li>
                <strong className="text-rax-heading">Business Transfers:</strong> In connection with a merger, acquisition, or sale of
                company assets, subject to standard confidentiality protections;
              </li>
              <li>
                <strong className="text-rax-heading">With Your Consent:</strong> In any other circumstances where you have provided
                explicit consent.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">6. Data Retention</h2>
            <p>
              We retain personal information for as long as necessary to fulfill the purposes described in
              this Privacy Policy, unless a longer retention period is required or permitted by law.
              When information is no longer needed, we delete or anonymize it in accordance with our
              data retention procedures.
            </p>
            <p className="mt-3">
              Note that on-chain data (blockchain transactions) is permanent and cannot be deleted by us
              or any other party.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">7. Data Security</h2>
            <p>
              We implement commercially reasonable technical and organizational measures to protect your
              information against unauthorized access, alteration, disclosure, or destruction. However,
              no method of transmission over the internet or electronic storage is 100% secure, and we
              cannot guarantee absolute security. You use the Service at your own risk.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">8. Third-Party Links and Services</h2>
            <p>
              The Service may contain links to third-party websites, DeFi protocols, or services not
              operated by us. This Privacy Policy does not apply to those third parties. We encourage
              you to review the privacy policies of any third-party services you access. We are not
              responsible for their privacy practices.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">9. Children's Privacy</h2>
            <p>
              The Service is not directed to individuals under the age of 18. We do not knowingly collect
              personal information from minors. If we become aware that a minor has provided us with
              personal information, we will take steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">10. International Transfers</h2>
            <p>
              RAX Protocol Foundation is incorporated in Switzerland. Your information may be processed
              in Switzerland or in other countries where our service providers operate. By using the Service,
              you consent to the transfer and processing of your information in jurisdictions that may have
              different data protection laws than your own.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">11. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have rights with respect to your personal information,
              including the right to:
            </p>
            <ul className="mt-3 list-disc space-y-1.5 pl-5">
              <li>Access the personal information we hold about you;</li>
              <li>Request correction of inaccurate information;</li>
              <li>Request deletion of your information (subject to legal obligations);</li>
              <li>Object to or restrict processing of your information;</li>
              <li>Data portability where applicable.</li>
            </ul>
            <p className="mt-3">
              To exercise these rights, please contact us at the address below. We will respond within
              the timeframe required by applicable law.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the
              "Last updated" date at the top of this page. We encourage you to review this policy
              periodically. Continued use of the Service after changes constitutes your acceptance
              of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">13. Governing Law</h2>
            <p>
              This Privacy Policy is governed by the laws of Switzerland. Any disputes
              relating to this Privacy Policy shall be subject to the exclusive jurisdiction of the
              courts of Zürich, Switzerland, or arbitration as described in our Terms of Service.
            </p>
          </section>

          <section>
            <h2 className="mb-3 font-heading text-lg font-semibold text-rax-heading">14. Contact Us</h2>
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy, please contact us:
            </p>
            <p className="mt-3">
              <strong className="text-rax-heading">RAX Protocol Foundation</strong><br />
              Bahnhofstrasse 42, 8001 Zürich, Switzerland<br />
              Email:{' '}
              <a href="mailto:info@raxprotocol.xyz" className="text-rax-cyan hover:text-rax-cyan/80 transition-colors">
                info@raxprotocol.xyz
              </a>
            </p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
